import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Divider, Stack, Typography, useTheme } from '@mui/material';

import { FolderType, GetOrganizationItemLocationType, IndustryDTO, useGetOrganizationItem } from '@/api/generated';
import HeaderProperty from '@/components/HeaderProperty';
import SeparatedList from '@/components/SeparatedList';
import GeneralStatus from '@/components/statuses/GeneralStatus/GeneralStatus';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { useIndustries } from '@/hooks/useIndustries';
import { useOffices } from '@/hooks/useOffices';
import { useOrganizationStructure } from '@/hooks/useOrganizationStructure';
import countries from '@/utils/countries';

interface OrganizationInfoProps {
  organizationSlug?: string;
  itemSlug?: string;
}

const OrganizationInfo: FC<OrganizationInfoProps> = ({ organizationSlug, itemSlug = organizationSlug }) => {
  const { t, i18n } = useTranslation(['organizations', 'common']);
  const { spacing } = useTheme();
  const { isMidotUser } = useIsMidotUser();

  const { location: locationData } = useOrganizationStructure(itemSlug!);

  const {
    data: organizationItem,
    isLoading,
    error
  } = useGetOrganizationItem(
    organizationSlug!,
    itemSlug!,
    {
      locationType: locationData?.type as GetOrganizationItemLocationType
    },
    {
      query: {
        enabled: !!organizationSlug && !!itemSlug && !!locationData?.type
      }
    }
  );

  const industry = organizationItem?.industry as string | IndustryDTO | undefined;
  const industryId = typeof industry === 'string' ? industry : industry?.id;

  const { officeMap } = useOffices();
  const { industryMap } = useIndustries();

  const industryLabel = useMemo(
    () => industryMap?.get(industryId ?? '')?.label ?? industryId ?? '',
    [industryId, industryMap]
  );

  if (!organizationSlug || !itemSlug || !locationData?.type) {
    return null;
  }

  if (isLoading) {
    return <CircularProgress size={16} />;
  }

  if (error || !organizationItem) {
    return (
      <Stack direction="row" justifyContent="start" gap={1}>
        <Typography variant="body3" color="error">
          {t('common:error.somethingWentWrong')}
        </Typography>
      </Stack>
    );
  }

  return (
    <SeparatedList
      separator={<Divider orientation="vertical" flexItem sx={{ m: spacing(0, 3) }} />}
      sx={{ mt: 1.25, '& .HeaderProperty-root': { m: 0, alignItems: 'center' } }}
    >
      {FolderType.SUB_FOLDER !== locationData?.type && organizationItem.status && (
        <HeaderProperty
          variant="body3"
          sx={{
            '& .IndicatorChip-root': { backgroundColor: 'transparent', height: 'unset' },
            '& .CircleIcon-root': { my: 0 },
            '& .MuiChip-label': { lineHeight: 1 },
            '& .HeaderProperty-description': { m: 0 }
          }}
          skipEmpty
        >
          <GeneralStatus value={organizationItem.status} />
        </HeaderProperty>
      )}
      {(FolderType.ORGANIZATION === locationData?.type || FolderType.SUB_ORGANIZATION === locationData?.type) &&
        organizationItem.offices &&
        isMidotUser && (
          <HeaderProperty variant="body3" label={t('layouts.organizations.details.info.office')} skipEmpty>
            {t('list', {
              val: organizationItem.offices.map(office => officeMap.get(office)?.name).filter(Boolean),
              ns: 'common'
            })}
          </HeaderProperty>
        )}
      {(organizationItem.type === FolderType.ORGANIZATION || FolderType.SUB_ORGANIZATION === locationData?.type) &&
        organizationItem.countries && (
          <HeaderProperty variant="body3" label={t('layouts.organizations.details.info.country')} skipEmpty>
            {t('list', {
              val: organizationItem.countries
                .map(country => countries.getName(country!, i18n.language)!)
                .filter(Boolean),
              ns: 'common'
            })}
          </HeaderProperty>
        )}
      {organizationItem.type === FolderType.ORGANIZATION && industryLabel && (
        <HeaderProperty variant="body3" skipEmpty>
          {`${industryLabel} ${t('layouts.organizations.details.info.industry')}`}
        </HeaderProperty>
      )}
      {Number.isFinite(organizationItem.userCount) &&
        (FolderType.ORGANIZATION === locationData?.type ||
          FolderType.SUB_ORGANIZATION === locationData?.type ||
          FolderType.FOLDER === locationData?.type) && (
          <HeaderProperty variant="body3" skipEmpty>
            {t('layouts.organizations.details.info.employees', { count: organizationItem.userCount })}
          </HeaderProperty>
        )}
    </SeparatedList>
  );
};

export default OrganizationInfo;
